import { loadStripe } from "@stripe/stripe-js";
import { createPaymentSession } from "../modules/Reducers/actions/paymentActions";
import {
  clearPendingContributionIntent,
  readPendingContributionIntent,
} from "../modules/constants/signupFlow";
import { getProfilePublicPath } from "./profilePaths";

function getStripeSessionData(response) {
  const sessionId =
    response?.id ||
    response?.sessionId ||
    response?.session_id ||
    response?.data?.id ||
    response?.data?.sessionId ||
    response?.data?.session_id;
  const sessionUrl =
    response?.url ||
    response?.checkout_url ||
    response?.data?.url ||
    response?.data?.checkout_url;
  return { sessionId, sessionUrl };
}

function getSuccessUrlFromIntent(intent) {
  const creation = intent?.creation || {};
  const creatorIdentifier =
    creation.creator_id ||
    creation.users?.user_id ||
    creation.users?.id;

  if (!creatorIdentifier) {
    const url = new URL(window.location.href);
    url.searchParams.set("success", "true");
    return url.toString();
  }

  const profileUser = creation.users;
  const path =
    getProfilePublicPath(profileUser) || `/profiles/${creatorIdentifier}`;
  const url = new URL(path, window.location.origin);
  url.searchParams.set("success", "true");
  return url.toString();
}

/**
 * Continues a contribution after auth using `hyk_pending_contribution_intent` in localStorage.
 * Clears the intent only after a Stripe session is ready to redirect.
 * @param {{ intent?: object } | undefined} options
 * @returns {Promise<{ success: boolean, redirecting?: boolean, reason?: string }>}
 */
export async function resumePendingContributionCheckout(options = {}) {
  const intent = options.intent ?? readPendingContributionIntent();
  if (!intent || (intent.source !== "contribute" && intent.source !== "order")) {
    return { success: false, reason: "no_intent" };
  }

  const amount = Number(intent.amount);
  if (!amount || amount <= 0) {
    return { success: false, reason: "no_amount" };
  }

  const type =
    intent.source === "order"
      ? "order"
      : intent.type === "MONTHLY"
        ? "subscription"
        : "payment";
  const creation = intent.creation || {};

  try {
    const stripe = await loadStripe(import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY);
    const payload = {
      type,
      amount,
      currency: "eur",
      success_url:
        intent.source === "order"
          ? intent.success_url || `${window.location.origin}/shipments`
          : getSuccessUrlFromIntent(intent),
      cancel_url: intent.cancel_url || window.location.href,
      creation: {
        id: creation.id ?? creation.creation_id,
        title: creation.title,
        description: creation.description,
        creator_id: creation.creator_id,
        imageUrl: creation.imageUrl,
        ...(creation.creation_type ? { creation_type: creation.creation_type } : {}),
      },
      ...(intent.source === "order" && Array.isArray(intent.items)
        ? { items: intent.items }
        : {}),
      ...(intent.source === "order" && intent.shipping_address_id
        ? { shipping_address_id: intent.shipping_address_id }
        : {}),
    };

    const sessionResponse = await createPaymentSession(payload);
    const { sessionId, sessionUrl } = getStripeSessionData(sessionResponse);

    if (!sessionUrl && !sessionId) {
      return { success: false, reason: "no_session" };
    }

    clearPendingContributionIntent();

    if (sessionUrl) {
      window.location.href = sessionUrl;
      return { success: true, redirecting: true };
    }

    const result = await stripe.redirectToCheckout({ sessionId });
    if (result.error) {
      console.error(result.error.message);
      return { success: false, reason: "stripe", error: result.error };
    }
    return { success: true, redirecting: true };
  } catch (error) {
    console.error("resumePendingContributionCheckout:", error);
    return { success: false, reason: "error", error };
  }
}
